import {
  Box,
  Button,
  Chip,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid2,
  IconButton,
  LinearProgress,
  Link,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  Quiz as QuizIcon,
  CheckCircle,
  Cancel,
  School,
  Timer,
  ArrowBack,
  ArrowDownward,
  ArrowUpward,
} from "@mui/icons-material";
import { useState, useEffect } from "react";

const ExhibitRenderer = ({ exhibits }) => {
  if (!exhibits || exhibits.length === 0) return null;
  return (
    <Stack spacing={2}>
      {exhibits.map((exhibit) => {
        if (exhibit.type === "table" && exhibit.table_data) {
          const { headers, rows } = exhibit.table_data;
          return (
            <Paper key={exhibit.id} variant="outlined" sx={{ overflow: "hidden" }}>
              {exhibit.caption && (
                <Typography variant="caption" color="text.secondary" sx={{ px: 2, pt: 1, display: "block" }}>
                  {exhibit.caption}
                </Typography>
              )}
              <TableContainer>
                <Table size="small">
                  {headers && (
                    <TableHead>
                      <TableRow>
                        {headers.map((h, i) => (
                          <TableCell key={i} sx={{ fontWeight: 600 }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                  )}
                  <TableBody>
                    {(rows ?? []).map((row, ri) => (
                      <TableRow key={ri}>
                        {row.map((cell, ci) => (
                          <TableCell key={ci}>{cell}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          );
        }
        if (exhibit.type === "code" && exhibit.content) {
          return (
            <Paper key={exhibit.id} variant="outlined" sx={{ overflow: "hidden" }}>
              {exhibit.caption && (
                <Typography variant="caption" color="text.secondary" sx={{ px: 2, pt: 1, display: "block" }}>
                  {exhibit.caption}
                </Typography>
              )}
              <Box
                component="pre"
                sx={{
                  p: 2,
                  m: 0,
                  overflow: "auto",
                  fontSize: "0.8rem",
                  fontFamily: "monospace",
                  bgcolor: "action.hover",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {exhibit.content}
              </Box>
            </Paper>
          );
        }
        return null;
      })}
    </Stack>
  );
};

const getExplanationText = (explanation) => {
  if (!explanation) return null;
  if (typeof explanation === "string") return explanation;
  return explanation.text ?? null;
};

const getExplanationUrl = (explanation) => {
  if (!explanation || typeof explanation === "string") return null;
  return explanation.reference_url ?? null;
};

const getExplanationTitle = (explanation) => {
  if (!explanation || typeof explanation === "string") return null;
  return explanation.reference_title ?? null;
};

const getPerOptionExplanations = (question) => {
  if (!question) return null;
  // Check both question-level and nested in explanation object
  if (question.per_option_explanations) return question.per_option_explanations;
  if (question.explanation && typeof question.explanation === "object") {
    return question.explanation.per_option_explanations ?? null;
  }
  return null;
};

const QuizSection = ({
  loading,
  error,
  quizSets,
  maxQuestions = 25,
  hasQuestions,
  question,
  questionIndex,
  questions,
  selectedOption,
  onSelectOption,
  showAnswer,
  onHideAnswer,
  answerLog,
  questionKey,
  correctOptions,
  progressPercent,
  attemptedCount,
  correctCount,
  accuracy,
  onResetQuiz,
  onPrevQuestion,
  onNextQuestion,
  quizComplete,
  mode,
  activeQuizIndex,
  onSelectQuiz,
  onEndQuiz,
  quizMode,
  onQuizModeChange,
  onSelectAndCheck,
  onCheckAnswer,
}) => {
  const theme = useTheme();
  const [selectedQuizIndex, setSelectedQuizIndex] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [modeChangeDialog, setModeChangeDialog] = useState({
    open: false,
    newMode: null,
  });
  const resolvedQuizMode = quizMode ?? "study";

  const questionType = question?.question_type ?? "single_correct_mcq";
  const isSingleChoice = [
    "single_correct_mcq",
    "true_false",
    "assertion_reason",
  ].includes(questionType);
  const isMultiChoice = questionType === "multiple_correct_mcq";
  const isHotspot = questionType === "hotspot";
  const isDragDrop = questionType === "drag_drop";
  const answerEntry = answerLog[questionKey];
  const isCorrect = Boolean(answerEntry?.isCorrect);

  const trueFalseOptions = [
    { id: "True", text: "True" },
    { id: "False", text: "False" },
  ];

  const normalizeTrueFalseLabel = (value) => {
    if (typeof value === "boolean") {
      return value ? "True" : "False";
    }
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      if (normalized === "true") {
        return "True";
      }
      if (normalized === "false") {
        return "False";
      }
      return value.trim();
    }
    return "";
  };

  const getOptionTextMap = (options = []) =>
    options.reduce((acc, option, index) => {
      if (typeof option === "string") {
        acc[String.fromCharCode(65 + index)] = option;
      } else {
        acc[option.id] = option.text;
      }
      return acc;
    }, {});

  const formatChoiceAnswer = (q, answer) => {
    const options =
      q?.question_type === "true_false" ? trueFalseOptions : q?.options || [];
    const map = getOptionTextMap(options);
    if (!answer) {
      return "Not answered";
    }
    if (Array.isArray(answer)) {
      return answer.map((id) => map[id] || id).join(", ");
    }
    return map[answer] || String(answer);
  };

  const formatMatchAnswer = (q, answer) => {
    if (!answer || typeof answer !== "object" || Array.isArray(answer)) {
      return "Not answered";
    }
    const aText = (q?.column_A || []).reduce((acc, item) => {
      acc[item.id] = item.text;
      return acc;
    }, {});
    const bText = (q?.column_B || []).reduce((acc, item) => {
      acc[item.id] = item.text;
      return acc;
    }, {});
    return (q?.column_A || [])
      .map(
        (item) =>
          `${aText[item.id] ?? item.id} -> ${bText[answer[item.id]] ?? answer[item.id] ?? "?"
          }`,
      )
      .join(" | ");
  };

  const formatSequenceAnswer = (answer) => {
    if (!Array.isArray(answer) || answer.length === 0) {
      return "Not answered";
    }
    return answer.join(" -> ");
  };

  const formatAnswer = (q, answer) => {
    const type = q?.question_type ?? "single_correct_mcq";
    if (type === "match_the_following") {
      return formatMatchAnswer(q, answer);
    }
    if (type === "sequence_order") {
      return formatSequenceAnswer(answer);
    }
    if (type === "short_answer") {
      return answer ? String(answer) : "Not answered";
    }
    if (type === "hotspot") {
      if (!answer || typeof answer !== "object" || Array.isArray(answer)) return "Not answered";
      return (q.hotspot_statements ?? []).map((st) => `${st.text} ${answer[st.id] ?? "?"}`).join(" | ");
    }
    if (type === "drag_drop") {
      if (!answer || typeof answer !== "object" || Array.isArray(answer)) return "Not answered";
      const pool = q.drag_drop_pool ?? [];
      return (q.drag_drop_targets ?? []).map((t) => {
        const item = pool.find((p) => p.id === answer[t.id]);
        return `${t.label}: ${item?.text ?? "?"}`;
      }).join(" | ");
    }
    return formatChoiceAnswer(q, answer);
  };

  const formatCorrectAnswer = (q) => {
    const type = q?.question_type ?? "single_correct_mcq";
    if (type === "match_the_following") {
      return formatMatchAnswer(q, q.correct_answer);
    }
    if (type === "sequence_order") {
      return formatSequenceAnswer(q.correct_order);
    }
    if (type === "short_answer") {
      return q.correct_answer ? String(q.correct_answer) : "Not available";
    }
    if (type === "true_false") {
      return normalizeTrueFalseLabel(q.correct_answer);
    }
    if (type === "hotspot") {
      return (q.hotspot_statements ?? []).map((st) => `${st.text} ${st.correct_answer}`).join(" | ");
    }
    if (type === "drag_drop") {
      const pool = q.drag_drop_pool ?? [];
      return (q.drag_drop_targets ?? []).map((t) => {
        const item = pool.find((p) => p.id === t.correct_item_id);
        return `${t.label}: ${item?.text ?? "?"}`;
      }).join(" | ");
    }
    return formatChoiceAnswer(q, q.correct_answer);
  };

  const isControlled = activeQuizIndex !== undefined;

  const clearSelection = () => {
    if (isControlled) {
      onSelectQuiz?.(null);
    }
    setSelectedQuizIndex(null);
  };

  const handleSelectQuiz = (index) => {
    if (!isControlled) {
      setSelectedQuizIndex(index);
    }
    onSelectQuiz?.(index);
  };

  // Reset showResults when quiz changes or is reset
  useEffect(() => {
    setShowResults(false);
  }, [selectedQuizIndex, activeQuizIndex]);

  // Reset showResults when quiz is reset
  useEffect(() => {
    if (!quizComplete) {
      setShowResults(false);
    }
  }, [quizComplete]);

  // Use prop if provided, otherwise use internal state
  const effectiveQuizIndex = isControlled ? activeQuizIndex : selectedQuizIndex;

  if (effectiveQuizIndex === null) {
    // Show quiz list only
    return (
      <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, height: "100%" }}>
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <QuizIcon color="primary" />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5">Quizzes</Typography>
              <Typography variant="body2" color="text.secondary">
                Practice MCQs with randomized questions and options.
              </Typography>
            </Box>
            <Chip
              label={loading ? "Loading..." : `${quizSets.length} sets`}
              color="secondary"
              variant="outlined"
            />
          </Stack>

          {error && (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          )}

          {quizSets.length === 0 && !loading && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1">No quizzes yet</Typography>
              <Typography variant="body2" color="text.secondary">
                Add quiz files for this grade to see them here.
              </Typography>
            </Paper>
          )}

          {quizSets.length > 0 && (
            <Stack spacing={1.5}>
              {quizSets.map((quiz, index) => (
                <Button
                  key={`${quiz.title}-${index}`}
                  variant="outlined"
                  color="primary"
                  onClick={() => handleSelectQuiz(index)}
                  sx={{ justifyContent: "space-between", textAlign: "left" }}
                >
                  <Stack alignItems="flex-start">
                    <Typography variant="subtitle2">{quiz.title}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {quiz.topic || "Practice and learn"}
                    </Typography>
                  </Stack>
                  <Chip
                    size="small"
                    label={`${Math.min(quiz.questions.length, maxQuestions)} Qs`}
                  />
                </Button>
              ))}
            </Stack>
          )}
        </Stack>
      </Paper>
    );
  }

  // Show selected quiz questions
  const quiz = quizSets[effectiveQuizIndex];

  if (!quiz) {
    return (
      <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, height: "100%" }}>
        <Typography>Quiz not found</Typography>
      </Paper>
    );
  }

  // Show results page
  if (showResults) {
    return (
      <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, height: "100%" }}>
        <Stack spacing={3}>
          {/* Results Header */}
          <Stack spacing={2}>
            <Button
              variant="outlined"
              onClick={() => setShowResults(false)}
              sx={{ alignSelf: "flex-start" }}
            >
              ← Back to Quiz
            </Button>

            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="h5">🎉 Quiz Results</Typography>
              <Chip
                label={resolvedQuizMode === "study" ? "Study Mode" : "Exam Mode"}
                color={resolvedQuizMode === "study" ? "primary" : "secondary"}
                size="small"
              />
            </Stack>

            <Typography variant="body2" color="text.secondary">
              {resolvedQuizMode === "study"
                ? "Review your performance and learn from your mistakes."
                : "Here's how you performed on this exam."}
            </Typography>
          </Stack>

          {/* Overall Statistics */}
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              bgcolor: alpha(theme.palette.success.main, 0.08),
              borderColor: theme.palette.success.main,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Overall Performance
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Grid2 container spacing={3}>
              <Grid2 size={{ xs: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  Total Questions
                </Typography>
                <Typography variant="h4">{questions.length}</Typography>
              </Grid2>
              <Grid2 size={{ xs: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  Attempted
                </Typography>
                <Typography variant="h4">{attemptedCount}</Typography>
              </Grid2>
              <Grid2 size={{ xs: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  Correct Answers
                </Typography>
                <Typography variant="h4" color="success.main">
                  {correctCount}
                </Typography>
              </Grid2>
              <Grid2 size={{ xs: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  Accuracy
                </Typography>
                <Typography variant="h4" color="primary.main">
                  {accuracy}%
                </Typography>
              </Grid2>
            </Grid2>
          </Paper>

          {/* Question Breakdown */}
          <Stack spacing={2}>
            <Typography variant="h6">Question Breakdown</Typography>

            {questions.map((q, idx) => {
              const qKey = q?.question_id ?? `${activeQuizIndex}-${idx}`;
              const answer = answerLog[qKey];
              const userAnswer = answer?.selectedOption;
              const isCorrect = answer?.isCorrect;
              const isAnswered = answer?.isChecked;

              return (
                <Paper
                  key={qKey}
                  variant="outlined"
                  sx={{
                    p: 2,
                    bgcolor: isAnswered
                      ? isCorrect
                        ? alpha(theme.palette.success.main, 0.04)
                        : alpha(theme.palette.error.main, 0.04)
                      : "transparent",
                    borderColor: isAnswered
                      ? isCorrect
                        ? theme.palette.success.main
                        : theme.palette.error.main
                      : "divider",
                  }}
                >
                  <Stack spacing={1.5}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      spacing={2}
                    >
                      <Stack spacing={0.5} flex={1}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="subtitle2">
                            Question {idx + 1}
                          </Typography>
                          {isAnswered && (
                            <Chip
                              size="small"
                              label={isCorrect ? "Correct" : "Incorrect"}
                              color={isCorrect ? "success" : "error"}
                              icon={
                                isCorrect ? (
                                  <CheckCircle fontSize="small" />
                                ) : (
                                  <Cancel fontSize="small" />
                                )
                              }
                            />
                          )}
                          {!isAnswered && (
                            <Chip
                              size="small"
                              label="Not Answered"
                              variant="outlined"
                            />
                          )}
                        </Stack>
                        <Typography variant="body2">
                          {q.question_text ||
                            (q.assertion && q.reason
                              ? `Assertion: ${q.assertion} | Reason: ${q.reason}`
                              : "Question unavailable")}
                        </Typography>
                      </Stack>
                    </Stack>

                    {isAnswered && (
                      <Stack spacing={1}>
                        <Stack direction="row" spacing={2} flexWrap="wrap">
                          <Typography variant="caption" color="text.secondary">
                            Your answer:{" "}
                            <Typography
                              component="span"
                              variant="caption"
                              color={isCorrect ? "success.main" : "error.main"}
                              fontWeight="medium"
                            >
                              {formatAnswer(q, userAnswer)}
                            </Typography>
                          </Typography>
                          {!isCorrect && (
                            <Typography variant="caption" color="text.secondary">
                              Correct answer:{" "}
                              <Typography
                                component="span"
                                variant="caption"
                                color="success.main"
                                fontWeight="medium"
                              >
                                {formatCorrectAnswer(q)}
                              </Typography>
                            </Typography>
                          )}
                        </Stack>
                      </Stack>
                    )}
                  </Stack>
                </Paper>
              );
            })}
          </Stack>

          {/* Actions */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            sx={{ width: "100%" }}
          >
            <Button
              variant="contained"
              onClick={() => {
                setShowResults(false);
                onResetQuiz();
              }}
              fullWidth
            >
              Retake Quiz
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setShowResults(false);
              }}
              fullWidth
            >
              Review Answers
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => {
                clearSelection();
                setShowResults(false);
                onEndQuiz?.();
              }}
              fullWidth
            >
              Back to Quizzes
            </Button>
          </Stack>
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, height: "100%" }}>
      {/* Mobile Header */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ mb: 2, display: { xs: "flex", sm: "none" } }}
      >
        <IconButton
          size="small"
          onClick={() => {
            clearSelection();
            onHideAnswer();
          }}
          sx={{ mr: 0.5 }}
        >
          <ArrowBack />
        </IconButton>
        <Box sx={{ flexGrow: 1 }} />
        <ToggleButtonGroup
          value={resolvedQuizMode}
          exclusive
          onChange={(e, newMode) => {
            if (newMode !== null && newMode !== resolvedQuizMode) {
              if (attemptedCount > 0) {
                setModeChangeDialog({ open: true, newMode });
              } else {
                onQuizModeChange(newMode);
              }
            }
          }}
          size="small"
          color="primary"
        >
          <ToggleButton value="study">
            <School sx={{ fontSize: "1rem" }} />
          </ToggleButton>
          <ToggleButton value="exam">
            <Timer sx={{ fontSize: "1rem" }} />
          </ToggleButton>
        </ToggleButtonGroup>
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          onClick={() => {
            setShowResults(true);
          }}
          sx={{ ml: 1, minWidth: "auto", px: 1.5 }}
        >
          End
        </Button>
      </Stack>

      {/* Desktop Header */}
      <Stack
        direction="row"
        spacing={1.5}
        sx={{ mb: 2, display: { xs: "none", sm: "flex" } }}
        flexWrap="wrap"
      >
        <Button
          variant="outlined"
          onClick={() => {
            clearSelection();
            onHideAnswer();
          }}
        >
          ← Back to quiz list
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => {
            setShowResults(true);
          }}
        >
          End Quiz
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <ToggleButtonGroup
          value={resolvedQuizMode}
          exclusive
          onChange={(e, newMode) => {
            if (newMode !== null && newMode !== resolvedQuizMode) {
              if (attemptedCount > 0) {
                setModeChangeDialog({ open: true, newMode });
              } else {
                onQuizModeChange(newMode);
              }
            }
          }}
          size="small"
          color="primary"
        >
          <ToggleButton value="study">
            <School sx={{ mr: 0.5, fontSize: "1.2rem" }} />
            Study
          </ToggleButton>
          <ToggleButton value="exam">
            <Timer sx={{ mr: 0.5, fontSize: "1.2rem" }} />
            Exam
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {hasQuestions && question ? (
        <Stack spacing={2.5}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <QuizIcon color="primary" />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6">{quiz.title}</Typography>
              {quiz.topic && (
                <Typography variant="body2" color="text.secondary">
                  {quiz.topic}
                </Typography>
              )}
            </Box>
          </Stack>

          <Paper
            variant="outlined"
            sx={{
              p: 2,
              bgcolor: alpha(
                theme.palette.primary.main,
                mode === "dark" ? 0.08 : 0.04,
              ),
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              mb={1}
              spacing={{ xs: 1, sm: 0 }}
            >
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="body2" color="text.secondary">
                  Progress
                </Typography>
                <Chip
                  size="small"
                  label={resolvedQuizMode === "study" ? "Study Mode" : "Exam Mode"}
                  color={resolvedQuizMode === "study" ? "primary" : "secondary"}
                  icon={resolvedQuizMode === "study" ? <School /> : <Timer />}
                />
                {
                  resolvedQuizMode === "study" ?
                    <Chip
                      size="small"
                      label={`Accuracy ${accuracy}%`}
                      variant="outlined"
                    />
                    : ""
                }
              </Stack>
              <Typography variant="body2" fontWeight="medium">
                {attemptedCount}/{questions.length} questions
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={progressPercent}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Paper>

          {quizComplete && (
            <Paper
              variant="outlined"
              sx={{ p: 2, borderColor: theme.palette.success.main }}
            >
              <Typography variant="h6">🎉 Quiz Complete!</Typography>
              <Typography variant="body2" color="text.secondary">
                You scored {correctCount} out of {questions.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {accuracy}% accuracy
              </Typography>
            </Paper>
          )}

          <Divider />

          <Box>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mb={1.5}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  Question {questionIndex + 1} of {questions.length}
                </Typography>
                {resolvedQuizMode === "exam" && answerLog[questionKey] && (
                  <Chip
                    size="small"
                    label="Answered"
                    color="success"
                    variant="filled"
                    sx={{ height: 20 }}
                  />
                )}
              </Stack>
              <Chip
                size="small"
                label={question.difficulty || "Medium"}
                color="secondary"
                variant="outlined"
              />
            </Stack>
            <Typography variant="h6" mb={2} sx={{ whiteSpace: "pre-line" }}>
              {question.series_stem && (
                <Typography variant="body2" color="text.secondary" component="div" sx={{ mb: 1, whiteSpace: "pre-line" }}>
                  {question.series_stem}
                </Typography>
              )}
              {question.question_text ||
                (question.assertion && question.reason
                  ? `Assertion: ${question.assertion}\nReason: ${question.reason}`
                  : "Question text unavailable")}
            </Typography>

            <ExhibitRenderer exhibits={question.exhibits} />

            {isSingleChoice && (
              <FormControl component="fieldset" fullWidth>
                <RadioGroup
                  value={typeof selectedOption === "string" ? selectedOption : ""}
                  onChange={(e) => {
                    const newOption = e.target.value;
                    // Keep selection controlled; optionally auto-check when handler is provided
                    onSelectOption?.(newOption);
                    if (onSelectAndCheck) {
                      onSelectAndCheck(newOption);
                    }
                  }}
                >
                  <Stack spacing={1}>
                    {(questionType === "true_false"
                      ? trueFalseOptions
                      : question.options || []
                    ).map((option, idx) => {
                      // Handle both object-based and string-based options
                      const optionId =
                        typeof option === "string"
                          ? String.fromCharCode(65 + idx)
                          : option.id;
                      const optionText =
                        typeof option === "string" ? option : option.text;
                      const isSelected = selectedOption === optionId;
                      const isCorrectOption = correctOptions.includes(optionId);
                      const isSelectionDisabled =
                        resolvedQuizMode === "study" && showAnswer;
                      const showSelectedFeedback =
                        resolvedQuizMode === "study" && showAnswer && isSelected;
                      const showCorrectAnswer =
                        resolvedQuizMode === "study" && showAnswer && isCorrectOption;

                      return (
                        <Paper
                          key={optionId}
                          variant="outlined"
                          sx={{
                            p: 1.5,
                            borderColor:
                              showSelectedFeedback && isCorrectOption
                                ? theme.palette.success.main
                                : showSelectedFeedback && !isCorrectOption
                                  ? theme.palette.error.main
                                  : showCorrectAnswer
                                    ? theme.palette.success.main
                                    : isSelected
                                      ? theme.palette.primary.main
                                      : "divider",
                            bgcolor:
                              showSelectedFeedback && isCorrectOption
                                ? alpha(theme.palette.success.main, 0.08)
                                : showSelectedFeedback && !isCorrectOption
                                  ? alpha(theme.palette.error.main, 0.08)
                                  : showCorrectAnswer
                                    ? alpha(theme.palette.success.main, 0.08)
                                    : isSelected
                                      ? alpha(theme.palette.primary.main, 0.08)
                                      : "transparent",
                          }}
                        >
                          <FormControlLabel
                            value={optionId}
                            control={<Radio disabled={isSelectionDisabled} />}
                            label={
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <Typography>{optionText}</Typography>
                                {showSelectedFeedback && isCorrectOption && (
                                  <CheckCircle color="success" fontSize="small" />
                                )}
                                {showSelectedFeedback && !isCorrectOption && (
                                  <Cancel color="error" fontSize="small" />
                                )}
                                {showCorrectAnswer && !isSelected && (
                                  <CheckCircle color="success" fontSize="small" />
                                )}
                              </Stack>
                            }
                            sx={{ width: "100%" }}
                          />
                        </Paper>
                      );
                    })}
                  </Stack>
                </RadioGroup>
              </FormControl>
            )}

            {isMultiChoice && (
              <FormControl component="fieldset" fullWidth>
                <Stack spacing={1}>
                  {(question.options || []).map((option, idx) => {
                    const optionId =
                      typeof option === "string"
                        ? String.fromCharCode(65 + idx)
                        : option.id;
                    const optionText =
                      typeof option === "string" ? option : option.text;
                    const selectedArray = Array.isArray(selectedOption)
                      ? selectedOption
                      : [];
                    const isSelected = selectedArray.includes(optionId);
                    const isCorrectOption = correctOptions.includes(optionId);
                    const isSelectionDisabled =
                      resolvedQuizMode === "study" && showAnswer;
                    const showSelectedFeedback =
                      resolvedQuizMode === "study" && showAnswer && isSelected;
                    const showCorrectAnswer =
                      resolvedQuizMode === "study" && showAnswer && isCorrectOption;

                    return (
                      <Paper
                        key={optionId}
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          borderColor:
                            showSelectedFeedback && isCorrectOption
                              ? theme.palette.success.main
                              : showSelectedFeedback && !isCorrectOption
                                ? theme.palette.error.main
                                : showCorrectAnswer
                                  ? theme.palette.success.main
                                  : isSelected
                                    ? theme.palette.primary.main
                                    : "divider",
                          bgcolor:
                            showSelectedFeedback && isCorrectOption
                              ? alpha(theme.palette.success.main, 0.08)
                              : showSelectedFeedback && !isCorrectOption
                                ? alpha(theme.palette.error.main, 0.08)
                                : showCorrectAnswer
                                  ? alpha(theme.palette.success.main, 0.08)
                                  : isSelected
                                    ? alpha(theme.palette.primary.main, 0.08)
                                    : "transparent",
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={isSelected}
                              disabled={isSelectionDisabled}
                              onChange={() => {
                                const next = isSelected
                                  ? selectedArray.filter((id) => id !== optionId)
                                  : [...selectedArray, optionId];
                                onSelectOption?.(next);
                              }}
                            />
                          }
                          label={
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography>{optionText}</Typography>
                              {showSelectedFeedback && isCorrectOption && (
                                <CheckCircle color="success" fontSize="small" />
                              )}
                              {showSelectedFeedback && !isCorrectOption && (
                                <Cancel color="error" fontSize="small" />
                              )}
                              {showCorrectAnswer && !isSelected && (
                                <CheckCircle color="success" fontSize="small" />
                              )}
                            </Stack>
                          }
                          sx={{ width: "100%" }}
                        />
                      </Paper>
                    );
                  })}
                </Stack>
              </FormControl>
            )}

            {questionType === "match_the_following" && (
              <Stack spacing={2}>
                {(question.column_A || []).map((item) => {
                  const matchSelections =
                    selectedOption &&
                      typeof selectedOption === "object" &&
                      !Array.isArray(selectedOption)
                      ? selectedOption
                      : {};
                  return (
                    <Grid2 container spacing={2} alignItems="center" key={item.id}>
                      <Grid2 xs={12} sm={6}>
                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                          <Typography>{item.text}</Typography>
                        </Paper>
                      </Grid2>
                      <Grid2 xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                          <Select
                            value={matchSelections[item.id] || ""}
                            displayEmpty
                            onChange={(e) => {
                              const next = {
                                ...matchSelections,
                                [item.id]: e.target.value,
                              };
                              onSelectOption?.(next);
                            }}
                          >
                            <MenuItem value="">
                              <em>Select match</em>
                            </MenuItem>
                            {(question.column_B || []).map((option) => (
                              <MenuItem key={option.id} value={option.id}>
                                {option.text}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid2>
                    </Grid2>
                  );
                })}
              </Stack>
            )}

            {questionType === "sequence_order" && (
              <Stack spacing={1.5}>
                {(Array.isArray(selectedOption)
                  ? selectedOption
                  : question.sequence_items || []
                ).map((item, index, list) => (
                  <Paper key={`${item}-${index}`} variant="outlined" sx={{ p: 1.5 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography sx={{ flex: 1 }}>{item}</Typography>
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (!Array.isArray(selectedOption)) {
                            onSelectOption?.(question.sequence_items || []);
                            return;
                          }
                          if (index === 0) {
                            return;
                          }
                          const next = [...selectedOption];
                          [next[index - 1], next[index]] = [
                            next[index],
                            next[index - 1],
                          ];
                          onSelectOption?.(next);
                        }}
                      >
                        <ArrowUpward fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (!Array.isArray(selectedOption)) {
                            onSelectOption?.(question.sequence_items || []);
                            return;
                          }
                          if (index === list.length - 1) {
                            return;
                          }
                          const next = [...selectedOption];
                          [next[index + 1], next[index]] = [
                            next[index],
                            next[index + 1],
                          ];
                          onSelectOption?.(next);
                        }}
                      >
                        <ArrowDownward fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            )}

            {questionType === "short_answer" && (
              <TextField
                value={typeof selectedOption === "string" ? selectedOption : ""}
                onChange={(e) => onSelectOption?.(e.target.value)}
                multiline
                minRows={3}
                fullWidth
                placeholder="Type your answer..."
              />
            )}

            {isHotspot && (
              <Stack spacing={2}>
                {(question.hotspot_statements ?? []).map((stmt) => {
                  const hotspotSelections =
                    selectedOption && typeof selectedOption === "object" && !Array.isArray(selectedOption)
                      ? selectedOption
                      : {};
                  const isChecked = answerEntry?.isChecked;
                  const stmtCorrect = isChecked && hotspotSelections[stmt.id] === stmt.correct_answer;
                  const stmtIncorrect = isChecked && hotspotSelections[stmt.id] && hotspotSelections[stmt.id] !== stmt.correct_answer;
                  return (
                    <Paper
                      key={stmt.id}
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderColor: stmtCorrect
                          ? theme.palette.success.main
                          : stmtIncorrect
                            ? theme.palette.error.main
                            : "divider",
                        bgcolor: stmtCorrect
                          ? alpha(theme.palette.success.main, 0.06)
                          : stmtIncorrect
                            ? alpha(theme.palette.error.main, 0.06)
                            : "transparent",
                      }}
                    >
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {stmt.text}
                      </Typography>
                      <FormControl size="small" fullWidth>
                        <Select
                          value={hotspotSelections[stmt.id] ?? ""}
                          displayEmpty
                          disabled={resolvedQuizMode === "study" && showAnswer}
                          onChange={(e) => {
                            const next = { ...hotspotSelections, [stmt.id]: e.target.value };
                            onSelectOption?.(next);
                          }}
                        >
                          <MenuItem value=""><em>Select answer</em></MenuItem>
                          {(stmt.answer_options ?? []).map((opt) => (
                            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {showAnswer && stmtIncorrect && (
                        <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: "block" }}>
                          Correct: {stmt.correct_answer}
                        </Typography>
                      )}
                      {showAnswer && stmt.explanation && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                          {stmt.explanation}
                        </Typography>
                      )}
                    </Paper>
                  );
                })}
              </Stack>
            )}

            {isDragDrop && (
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  Select the correct item for each target:
                </Typography>
                {(question.drag_drop_targets ?? []).map((target) => {
                  const ddSelections =
                    selectedOption && typeof selectedOption === "object" && !Array.isArray(selectedOption)
                      ? selectedOption
                      : {};
                  const isChecked = answerEntry?.isChecked;
                  const selected = ddSelections[target.id];
                  const targetCorrect = isChecked && selected === target.correct_item_id;
                  const targetIncorrect = isChecked && selected && selected !== target.correct_item_id;
                  const pool = question.drag_drop_pool ?? [];
                  const correctItemText = pool.find((p) => p.id === target.correct_item_id)?.text;
                  return (
                    <Paper
                      key={target.id}
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderColor: targetCorrect
                          ? theme.palette.success.main
                          : targetIncorrect
                            ? theme.palette.error.main
                            : "divider",
                        bgcolor: targetCorrect
                          ? alpha(theme.palette.success.main, 0.06)
                          : targetIncorrect
                            ? alpha(theme.palette.error.main, 0.06)
                            : "transparent",
                      }}
                    >
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {target.label}
                      </Typography>
                      <FormControl size="small" fullWidth>
                        <Select
                          value={ddSelections[target.id] ?? ""}
                          displayEmpty
                          disabled={resolvedQuizMode === "study" && showAnswer}
                          onChange={(e) => {
                            const next = { ...ddSelections, [target.id]: e.target.value };
                            onSelectOption?.(next);
                          }}
                        >
                          <MenuItem value=""><em>Select item</em></MenuItem>
                          {pool.map((item) => (
                            <MenuItem key={item.id} value={item.id}>{item.text}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {showAnswer && targetIncorrect && correctItemText && (
                        <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: "block" }}>
                          Correct: {correctItemText}
                        </Typography>
                      )}
                    </Paper>
                  );
                })}
              </Stack>
            )}

            {!isSingleChoice && (
              <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  onClick={() => {
                    const answerValue =
                      questionType === "match_the_following"
                        ? selectedOption
                        : questionType === "sequence_order"
                          ? Array.isArray(selectedOption)
                            ? selectedOption
                            : question.sequence_items || []
                          : selectedOption;
                    onCheckAnswer?.(answerValue);
                  }}
                  disabled={
                    (resolvedQuizMode === "study" && showAnswer) ||
                    (questionType === "match_the_following"
                      ? !(
                        selectedOption &&
                        typeof selectedOption === "object" &&
                        !Array.isArray(selectedOption) &&
                        (question.column_A || []).every(
                          (item) => selectedOption[item.id],
                        )
                      )
                      : questionType === "sequence_order"
                        ? !(
                          Array.isArray(selectedOption) &&
                          selectedOption.length > 0
                        )
                        : questionType === "short_answer"
                          ? !(
                            typeof selectedOption === "string" &&
                            selectedOption.trim().length > 0
                          )
                          : questionType === "multiple_correct_mcq"
                            ? !(
                              Array.isArray(selectedOption) &&
                              selectedOption.length > 0
                            )
                            : questionType === "hotspot"
                              ? !(
                                selectedOption &&
                                typeof selectedOption === "object" &&
                                !Array.isArray(selectedOption) &&
                                (question.hotspot_statements ?? []).every(
                                  (st) => selectedOption[st.id],
                                )
                              )
                              : questionType === "drag_drop"
                                ? !(
                                  selectedOption &&
                                  typeof selectedOption === "object" &&
                                  !Array.isArray(selectedOption) &&
                                  (question.drag_drop_targets ?? []).every(
                                    (t) => selectedOption[t.id],
                                  )
                                )
                                : true)
                  }
                >
                  Check Answer
                </Button>
              </Stack>
            )}

            {!isSingleChoice && questionType !== "multiple_correct_mcq" && !isHotspot && !isDragDrop && showAnswer && (
              <Paper
                variant="outlined"
                sx={{
                  mt: 2,
                  p: 1.5,
                  borderColor: isCorrect
                    ? theme.palette.success.main
                    : theme.palette.error.main,
                  bgcolor: isCorrect
                    ? alpha(theme.palette.success.main, 0.06)
                    : alpha(theme.palette.error.main, 0.06),
                }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  {isCorrect ? "Correct" : "Incorrect"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Your answer: {formatAnswer(question, selectedOption)}
                </Typography>
                {!isCorrect && (
                  <Typography variant="body2" color="text.secondary">
                    Correct answer: {formatCorrectAnswer(question)}
                  </Typography>
                )}
              </Paper>
            )}

            {(isHotspot || isDragDrop) && showAnswer && (
              <Paper
                variant="outlined"
                sx={{
                  mt: 2,
                  p: 1.5,
                  borderColor: isCorrect
                    ? theme.palette.success.main
                    : theme.palette.error.main,
                  bgcolor: isCorrect
                    ? alpha(theme.palette.success.main, 0.06)
                    : alpha(theme.palette.error.main, 0.06),
                }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  {isCorrect ? "All Correct" : "Some answers are incorrect"}
                </Typography>
              </Paper>
            )}

            {showAnswer && question.explanation && (
              <Paper
                variant="outlined"
                sx={{ mt: 2, p: 1.5, borderColor: "divider" }}
              >
                <Stack spacing={1}>
                  {getExplanationText(question.explanation) && (
                    <Typography variant="body2" color="text.secondary">
                      {getExplanationText(question.explanation)}
                    </Typography>
                  )}
                  {getPerOptionExplanations(question) && (
                    <Stack spacing={0.5} sx={{ mt: 1 }}>
                      <Typography variant="caption" fontWeight={600}>Per-option explanations:</Typography>
                      {Object.entries(getPerOptionExplanations(question)).map(([key, text]) => (
                        <Typography key={key} variant="caption" color="text.secondary">
                          <strong>{key}:</strong> {text}
                        </Typography>
                      ))}
                    </Stack>
                  )}
                  {getExplanationUrl(question.explanation) && (
                    <Link
                      href={getExplanationUrl(question.explanation)}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="caption"
                    >
                      {getExplanationTitle(question.explanation) || "Reference"}
                    </Link>
                  )}
                </Stack>
              </Paper>
            )}
          </Box>

          <Divider />

          <Stack direction="row" spacing={1.5} flexWrap="wrap">
            <Button
              variant="outlined"
              onClick={onPrevQuestion}
              disabled={questionIndex === 0}
            >
              ← Previous
            </Button>
            {resolvedQuizMode === "study" && (
              <Button variant="outlined" onClick={onResetQuiz}>
                Reset Quiz
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={onNextQuestion}
              disabled={questionIndex === questions.length - 1}
            >
              Next →
            </Button>
          </Stack>
        </Stack>
      ) : (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1">No questions available</Typography>
          <Typography variant="body2" color="text.secondary">
            This quiz doesn't have any questions yet.
          </Typography>
        </Paper>
      )}

      {/* Mode Change Confirmation Dialog */}
      <Dialog
        open={modeChangeDialog.open}
        onClose={() => setModeChangeDialog({ open: false, newMode: null })}
      >
        <DialogTitle>Switch Quiz Mode?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Switching to{" "}
            {modeChangeDialog.newMode === "exam" ? "Exam" : "Study"} mode will
            reset your progress. All answers will be cleared. Do you want to
            continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setModeChangeDialog({ open: false, newMode: null })}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              onQuizModeChange(modeChangeDialog.newMode);
              setModeChangeDialog({ open: false, newMode: null });
            }}
            variant="contained"
            color="primary"
          >
            Switch Mode
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default QuizSection;
